import { useState } from 'react';
import { collection, addDoc, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { useLanguage } from '@/contexts/LanguageContext';
import { normalizePhone, phoneQueryVariants, pickUnambiguousUserId } from '@/lib/accountDeletion/identity';
import { Globe, Trash2, CheckCircle2, Loader2 } from 'lucide-react';

const t = {
  en: {
    title: 'Delete Account Request',
    subtitle: 'Submit a request to delete your Namat account and all associated data.',
    phoneLabel: 'Phone Number',
    phonePlaceholder: '05XXXXXXXX',
    emailLabel: 'Email Address (optional)',
    emailPlaceholder: 'your@email.com',
    reasonLabel: 'Reason for Deletion (optional)',
    reasonPlaceholder: 'Tell us why you want to delete your account...',
    submitButton: 'Submit Deletion Request',
    submitting: 'Submitting...',
    successTitle: 'Request Submitted',
    // No confirmation message is sent — nothing in the system sends one — so do
    // not promise one here.
    successMessage: 'Your account deletion request has been submitted successfully. Our team will review it and process your request within 7 business days. We do not send a confirmation message: once the request is processed, your account is deleted and you will no longer be able to sign in. If you can still sign in after 7 business days, please submit the request again.',
    errorMessage: 'Failed to submit your request. Please try again.',
    dataNotice: 'What data will be deleted:',
    dataItems: [
      'Your profile information (name, phone, email)',
      'Health measurements you recorded (sleep, meals, activity, fasting)',
      'Habit tracking data and progress',
      'Reward points and history',
      'Group memberships, your messages and the files you sent',
      'Your appointments and coaching session records',
      'Your sign-in account, so you can no longer log in',
    ],
    retainedNotice: 'What will be kept:',
    retainedItems: [
      'Clinical examination records created by a clinic or examiner, which that organisation is required to keep',
      'Anonymized aggregate data used for statistics',
    ],
    retentionNotice: 'Deletion is permanent and cannot be undone.',
    backToHome: 'Back to Home',
    requiredField: 'Phone number is required',
    appName: 'Namat',
    appDescription: 'Promoting Healthy Living',
  },
  ar: {
    title: 'طلب حذف الحساب',
    subtitle: 'قدّم طلبًا لحذف حسابك في نمط وجميع البيانات المرتبطة به.',
    phoneLabel: 'رقم الجوال',
    phonePlaceholder: '05XXXXXXXX',
    emailLabel: 'البريد الإلكتروني (اختياري)',
    emailPlaceholder: 'your@email.com',
    reasonLabel: 'سبب الحذف (اختياري)',
    reasonPlaceholder: 'أخبرنا لماذا تريد حذف حسابك...',
    submitButton: 'إرسال طلب الحذف',
    submitting: 'جاري الإرسال...',
    successTitle: 'تم إرسال الطلب',
    successMessage: 'تم إرسال طلب حذف حسابك بنجاح. سيقوم فريقنا بمراجعته ومعالجة طلبك خلال 7 أيام عمل. لا نرسل رسالة تأكيد: بمجرد معالجة الطلب يُحذف حسابك ولن تتمكن من تسجيل الدخول. إذا كنت لا تزال قادرًا على تسجيل الدخول بعد 7 أيام عمل، يرجى إرسال الطلب مرة أخرى.',
    errorMessage: 'فشل في إرسال الطلب. يرجى المحاولة مرة أخرى.',
    dataNotice: 'البيانات التي سيتم حذفها:',
    dataItems: [
      'معلومات ملفك الشخصي (الاسم، الجوال، البريد)',
      'القياسات الصحية التي سجّلتها (النوم، الوجبات، النشاط، الصيام)',
      'بيانات تتبع العادات والتقدم',
      'نقاط المكافآت والسجل',
      'عضويات المجموعات ورسائلك والملفات التي أرسلتها',
      'مواعيدك وسجلات جلسات التدريب الصحي',
      'حساب الدخول، فلن تتمكن من تسجيل الدخول بعد ذلك',
    ],
    retainedNotice: 'البيانات التي سيتم الاحتفاظ بها:',
    retainedItems: [
      'سجلات الفحوصات السريرية التي أنشأها فاحص أو جهة صحية، والتي تلتزم تلك الجهة بحفظها',
      'بيانات مجمعة مجهولة الهوية تُستخدم لأغراض إحصائية',
    ],
    retentionNotice: 'الحذف نهائي ولا يمكن التراجع عنه.',
    backToHome: 'العودة للرئيسية',
    requiredField: 'رقم الجوال مطلوب',
    appName: 'نمط',
    appDescription: 'لتعزيز الحياة الصحية',
  },
};

/**
 * Resolve the typed phone to exactly one `users` document id, or null.
 *
 * Returns null on no match, on several matches, and on any lookup error — the
 * server-side callable refuses ambiguous requests, so a null here just means
 * "unknown", never "delete whoever". Numbers may be stored as `phone` or
 * `phoneNumber`, in local or E.164 shape, so every variant is queried.
 */
async function resolveUserIdForPhone(rawPhone: string): Promise<string | null> {
  const variants = phoneQueryVariants(rawPhone);
  if (variants.length === 0) return null;

  try {
    const ids: string[] = [];
    for (const field of ['phone', 'phoneNumber'] as const) {
      const snap = await getDocs(query(collection(db, 'users'), where(field, 'in', variants)));
      snap.docs.forEach((d) => ids.push(d.id));
    }
    const pick = pickUnambiguousUserId(ids);
    return pick.ok ? pick.userId : null;
  } catch {
    return null;
  }
}

export function DeleteAccountPage() {
  const { language, setLanguage } = useLanguage();
  const lang = language === 'ar' ? 'ar' : 'en';
  const txt = t[lang];
  const isRTL = lang === 'ar';

  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!phone.trim()) {
      setError(txt.requiredField);
      return;
    }

    setStatus('submitting');
    try {
      // Capture identity NOW, while the person typing is the person who knows
      // the number. Stored only when the phone matches exactly one user —
      // several matches (or none) are left for approveAccountDeletion to refuse
      // rather than guessed at here. A failure to look up must never block the
      // request itself; the callable can still resolve by phone.
      const userId = await resolveUserIdForPhone(phone);

      await addDoc(collection(db, 'accountDeletionRequests'), {
        phone: phone.trim(),
        normalizedPhone: normalizePhone(phone) || null,
        userId,
        // This page needs no sign-in and no one-time code, so NOTHING here
        // proves the person typing owns this number. Record that explicitly so
        // the approving admin is told rather than left to assume, and so an OTP
        // step can later attach a server-signed `phoneOwnershipProof` that
        // flips it. A value written here is only ever a claim — the server
        // re-evaluates it (see functions/accountDeletion/plan.js).
        phoneOwnershipProven: false,
        phoneOwnershipProof: null,
        email: email.trim() || null,
        reason: reason.trim() || null,
        status: 'pending',
        createdAt: Timestamp.now(),
        processedAt: null,
        processedBy: null,
      });
      setStatus('success');
    } catch {
      setStatus('error');
      setError(txt.errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f0faf0] to-white" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#7dda55] flex items-center justify-center">
              <span className="text-white font-bold text-sm">ن</span>
            </div>
            <div>
              <span className="font-semibold text-[#004250]">{txt.appName}</span>
              <span className="text-xs text-gray-500 block leading-none">{txt.appDescription}</span>
            </div>
          </div>
          <button
            onClick={() => setLanguage(lang === 'ar' ? 'en' : 'ar')}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#004250] transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-100"
          >
            <Globe className="w-4 h-4" />
            {lang === 'ar' ? 'English' : 'العربية'}
          </button>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-12">
        {status === 'success' ? (
          <div className="text-center space-y-6 p-8 bg-white rounded-2xl shadow-sm border">
            <CheckCircle2 className="w-16 h-16 text-[#7dda55] mx-auto" />
            <h2 className="text-2xl font-bold text-[#004250]">{txt.successTitle}</h2>
            <p className="text-gray-600 leading-relaxed">{txt.successMessage}</p>
            <a
              href="/landing"
              className="inline-block mt-4 px-6 py-2.5 bg-[#7dda55] text-white font-medium rounded-lg hover:bg-[#6cc84a] transition-colors"
            >
              {txt.backToHome}
            </a>
          </div>
        ) : (
          <>
            {/* Title */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-red-50 rounded-full mb-4">
                <Trash2 className="w-7 h-7 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-[#004250] mb-2">{txt.title}</h1>
              <p className="text-gray-500">{txt.subtitle}</p>
            </div>

            {/* Data Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
              <p className="font-semibold text-amber-800 mb-3">{txt.dataNotice}</p>
              <ul className="space-y-1.5">
                {txt.dataItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-amber-700">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-4 border-t border-amber-200 pt-3">
                <p className="font-semibold text-amber-800 mb-2">{txt.retainedNotice}</p>
                <ul className="space-y-1.5">
                  {txt.retainedItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-amber-700">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-xs text-amber-600 mt-3 border-t border-amber-200 pt-3">
                {txt.retentionNotice}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#004250] mb-1.5">
                  {txt.phoneLabel} <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={txt.phonePlaceholder}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7dda55]/50 focus:border-[#7dda55] transition-colors text-left"
                  dir="ltr"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#004250] mb-1.5">
                  {txt.emailLabel}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={txt.emailPlaceholder}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7dda55]/50 focus:border-[#7dda55] transition-colors text-left"
                  dir="ltr"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#004250] mb-1.5">
                  {txt.reasonLabel}
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={txt.reasonPlaceholder}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7dda55]/50 focus:border-[#7dda55] transition-colors resize-none"
                />
              </div>

              {error && (
                <p className="text-sm text-red-500 font-medium">{error}</p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {status === 'submitting' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {txt.submitting}
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    {txt.submitButton}
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
